import React, { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'
import {Search, RefreshCw, Building2, User, Phone, Mail, CreditCard, Tag, ChevronDown, X, Check, Edit3, Save, TrendingUp, Calendar, Percent} from 'lucide-react'

interface B2BCustomer {
  _id: string
  userId?: string
  userName: string
  email: string
  phone?: string
  customerType: string
  companyName?: string
  taxId?: string
  contactPerson?: string
  discountRate?: number
  creditDays?: number
  monthlyBudget?: number
  memberLevel: string
  status: string
  totalSpent: number
  orderCount: number
  createdAt: string
  updatedAt: string
}

const creditDaysOptions = [
  { value: 7, label: 'NET 7' },
  { value: 15, label: 'NET 15' },
  { value: 30, label: 'NET 30' },
  { value: 45, label: 'NET 45' },
]

const B2BAccountsManager: React.FC = () => {
  const [customers, setCustomers] = useState<B2BCustomer[]>([])
  const [filtered, setFiltered] = useState<B2BCustomer[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<B2BCustomer | null>(null)
  const [showDetail, setShowDetail] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editState, setEditState] = useState<Partial<B2BCustomer>>({})

  useEffect(() => { fetchCustomers() }, [])

  useEffect(() => {
    if (!search) {
      setFiltered(customers)
      return
    }
    const q = search.toLowerCase()
    setFiltered(customers.filter(c =>
      c.userName?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.companyName?.toLowerCase().includes(q) ||
      c.taxId?.includes(q) ||
      c.phone?.includes(q)
    ))
  }, [customers, search])

  const fetchCustomers = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.customers.list({
        filter: { customerType: 'b2b' },
        sort: { createdAt: -1 },
        limit: 200,
      })
      setCustomers(list || [])
    } catch (e) {
      console.error('Failed to fetch B2B customers:', e)
    } finally {
      setLoading(false)
    }
  }

  const openDetail = (c: B2BCustomer) => {
    setSelected(c)
    setEditState({
      companyName: c.companyName || '',
      taxId: c.taxId || '',
      contactPerson: c.contactPerson || '',
      discountRate: c.discountRate ?? 1,
      creditDays: c.creditDays ?? 30,
      monthlyBudget: c.monthlyBudget ?? 0,
      status: c.status,
    })
    setShowDetail(true)
  }

  const saveDetail = async () => {
    if (!selected) return
    setSaving(true)
    try {
      await lumi.entities.customers.update(selected._id, {
        ...editState,
        updatedAt: new Date().toISOString(),
      })
      setCustomers(prev => prev.map(c =>
        c._id === selected._id ? { ...c, ...editState } : c
      ))
      setSelected(prev => prev ? { ...prev, ...editState } : null)
      setShowDetail(false)
    } catch (e) {
      console.error('Save failed:', e)
    } finally {
      setSaving(false)
    }
  }

  const stats = {
    total: customers.length,
    active: customers.filter(c => c.status === 'active').length,
    totalSpent: customers.reduce((sum, c) => sum + (c.totalSpent || 0), 0),
  }

  return (
    <div className="p-8 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-light tracking-wider mb-1">B2B 帳戶管理</h2>
          <p className="text-gray-400 text-sm">商務客戶 · 折扣設定 · 月結管理</p>
        </div>
        <button
          onClick={fetchCustomers}
          className="flex items-center gap-2 px-4 py-2 border border-gray-700 text-gray-300 hover:border-gray-500 transition-colors"
        >
          <RefreshCw size={16} />
          <span className="text-sm">重整</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'B2B 客戶', value: stats.total, color: 'text-blue-400' },
          { label: '活躍中', value: stats.active, color: 'text-green-400' },
          { label: '累積消費', value: `NT$ ${stats.totalSpent.toLocaleString()}`, color: 'text-amber-400' },
        ].map(s => (
          <div key={s.label} className="bg-gray-950 border border-gray-800 p-4">
            <div className={`text-2xl font-light mb-1 ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-500">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="搜尋客戶名稱、Email、公司、統編..."
          className="w-full pl-9 pr-4 py-2.5 bg-gray-950 border border-gray-800 text-gray-100 text-sm focus:outline-none focus:border-gray-600"
        />
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="text-center py-16 text-gray-500">載入中...</div>
        ) : (
          <div className="bg-gray-950 border border-gray-800 overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-800">
                <tr className="text-left">
                  <th className="px-5 py-4 text-xs font-light text-gray-400 uppercase tracking-wider">客戶</th>
                  <th className="px-5 py-4 text-xs font-light text-gray-400 uppercase tracking-wider">公司</th>
                  <th className="px-5 py-4 text-xs font-light text-gray-400 uppercase tracking-wider">折扣率</th>
                  <th className="px-5 py-4 text-xs font-light text-gray-400 uppercase tracking-wider">月結</th>
                  <th className="px-5 py-4 text-xs font-light text-gray-400 uppercase tracking-wider">累積消費</th>
                  <th className="px-5 py-4 text-xs font-light text-gray-400 uppercase tracking-wider">狀態</th>
                  <th className="px-5 py-4 text-xs font-light text-gray-400 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c._id} className="border-b border-gray-800 hover:bg-gray-900 transition-colors">
                    <td className="px-5 py-4">
                      <div className="font-light text-gray-100">{c.userName}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{c.email}</div>
                      {c.phone && <div className="text-xs text-gray-600">{c.phone}</div>}
                    </td>
                    <td className="px-5 py-4">
                      <div className="text-sm text-gray-300 font-light">{c.companyName || '—'}</div>
                      {c.taxId && <div className="text-xs text-gray-500">統編：{c.taxId}</div>}
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm font-light text-emerald-400">
                        {c.discountRate ? `${Math.round(c.discountRate * 100)}%` : '—'}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm font-light text-blue-400">
                        {c.creditDays ? `NET ${c.creditDays}` : '—'}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm font-light text-gray-300">
                        NT$ {(c.totalSpent || 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-xs px-2 py-1 ${
                        c.status === 'active' ? 'bg-green-900 text-green-300' :
                        c.status === 'inactive' ? 'bg-gray-800 text-gray-400' :
                        'bg-red-900 text-red-300'
                      }`}>
                        {c.status === 'active' ? '活躍' : c.status === 'inactive' ? '停用' : '黑名單'}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => openDetail(c)}
                        className="px-3 py-1.5 border border-gray-700 text-gray-300 text-xs hover:border-gray-500 transition-colors flex items-center gap-1"
                      >
                        <Edit3 size={12} />
                        編輯
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-16 text-gray-500 text-sm">
                {customers.length === 0
                  ? '尚無 B2B 客戶資料。審核通過 B2B 詢問後客戶將自動出現在此。'
                  : '無符合條件的資料'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Detail / Edit Modal */}
      {showDetail && selected && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-950 border border-gray-800 w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-800 flex items-start justify-between flex-shrink-0">
              <div>
                <h3 className="text-xl font-light tracking-wider text-gray-100">{selected.userName}</h3>
                <p className="text-sm text-gray-400 mt-1">{selected.email}</p>
              </div>
              <button onClick={() => setShowDetail(false)} className="text-gray-500 hover:text-gray-200 ml-4">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto flex-1">
              {/* 基本資訊 */}
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">公司資訊</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">公司名稱</label>
                    <input
                      type="text"
                      value={editState.companyName || ''}
                      onChange={e => setEditState(prev => ({ ...prev, companyName: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-gray-300 text-sm focus:outline-none focus:border-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">統一編號</label>
                    <input
                      type="text"
                      value={editState.taxId || ''}
                      onChange={e => setEditState(prev => ({ ...prev, taxId: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-gray-300 text-sm focus:outline-none focus:border-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">聯絡人</label>
                    <input
                      type="text"
                      value={editState.contactPerson || ''}
                      onChange={e => setEditState(prev => ({ ...prev, contactPerson: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-gray-300 text-sm focus:outline-none focus:border-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">每月預算 (NT$)</label>
                    <input
                      type="number"
                      value={editState.monthlyBudget || 0}
                      onChange={e => setEditState(prev => ({ ...prev, monthlyBudget: Number(e.target.value) }))}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-gray-300 text-sm focus:outline-none focus:border-gray-500"
                    />
                  </div>
                </div>
              </div>

              {/* 商務條件 */}
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">商務條件</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      專屬折扣率（如 0.85 = 85折）
                    </label>
                    <input
                      type="number"
                      min="0.1"
                      max="1"
                      step="0.01"
                      value={editState.discountRate ?? 1}
                      onChange={e => setEditState(prev => ({ ...prev, discountRate: Number(e.target.value) }))}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-gray-300 text-sm focus:outline-none focus:border-gray-500"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      顯示為：{Math.round((editState.discountRate ?? 1) * 100)}%
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">月結天數</label>
                    <select
                      value={editState.creditDays ?? 30}
                      onChange={e => setEditState(prev => ({ ...prev, creditDays: Number(e.target.value) }))}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-gray-300 text-sm focus:outline-none focus:border-gray-500"
                    >
                      {creditDaysOptions.map(o => (
                        <option key={o.value} value={o.value} className="bg-gray-900">{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* 帳號狀態 */}
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">帳號狀態</div>
                <div className="flex gap-2">
                  {['active', 'inactive', 'blacklist'].map(s => (
                    <button
                      key={s}
                      onClick={() => setEditState(prev => ({ ...prev, status: s }))}
                      className={`px-4 py-2 text-xs transition-all ${
                        editState.status === s
                          ? s === 'active' ? 'bg-green-700 text-white' :
                            s === 'inactive' ? 'bg-gray-700 text-white' : 'bg-red-800 text-white'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {s === 'active' ? '活躍' : s === 'inactive' ? '停用' : '黑名單'}
                    </button>
                  ))}
                </div>
              </div>

              {/* 消費統計（唯讀） */}
              <div className="p-4 bg-gray-900 border border-gray-800">
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">消費統計（唯讀）</div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">累積消費：</span>
                    <span className="text-gray-300 font-light">NT$ {(selected.totalSpent || 0).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">訂單數：</span>
                    <span className="text-gray-300 font-light">{selected.orderCount || 0} 筆</span>
                  </div>
                  <div>
                    <span className="text-gray-500">加入時間：</span>
                    <span className="text-gray-400 font-light text-xs">
                      {new Date(selected.createdAt).toLocaleDateString('zh-TW')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-800 flex justify-end gap-3 flex-shrink-0">
              <button
                onClick={() => setShowDetail(false)}
                className="px-5 py-2 border border-gray-700 text-gray-300 text-sm hover:border-gray-500 transition-colors"
              >
                取消
              </button>
              <button
                onClick={saveDetail}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                儲存設定
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default B2BAccountsManager
